import { create } from 'zustand';

import { draftLogbookEntryForTrack } from '@/features/logbook';
import type { Track, TrackPoints } from '@/lib/db';

import { importNormalisedTrack } from './import-track';
import type { ParseClient } from './parse-client';
import { createParseClient } from './parse-client';

/**
 * The bulk import queue.
 *
 * `PLAN.md` §6 sets the bar: a folder of fifty mixed files imports in one drop
 * with a per-file result list. Two things follow from that and shape this
 * store.
 *
 * **Every file gets a row, and a row never disappears.** A batch where six
 * files failed and forty-four worked is a normal outcome, not an error state,
 * and the six are the whole reason the list exists. So failure is a row status,
 * not an exception, and nothing is auto-dismissed.
 *
 * **Files are processed one at a time.** Fifty 3.5 MB KMLs decoded at once is a
 * few hundred megabytes of strings and DOM, and the duplicate check has to see
 * the *previous* file's write to catch two copies in one drop. Sequential is
 * both safer and correct; the parse is off-thread, so the UI stays live anyway.
 */

export type QueueItemStatus =
  'queued' | 'reading' | 'parsing' | 'saving' | 'imported' | 'duplicate' | 'failed';

export interface QueueItem {
  id: string;
  filename: string;
  sizeBytes: number;
  status: QueueItemStatus;
  /** One line explaining the status, in the pilot's words. */
  message?: string;
  /** What the parser recognised, or why it failed. Shown when expanded. */
  detail: string[];
  track?: Track;
}

export interface ImportQueueState {
  items: QueueItem[];
  /** Whether a batch is running, so the drop zone can say so. */
  busy: boolean;
  /** False when parsing had to fall back to the main thread. */
  offThread: boolean;
  enqueue: (files: readonly File[]) => Promise<void>;
  clearFinished: () => void;
  reset: () => void;
}

/** Overridable so tests need neither a worker nor a real `File`. */
export interface QueueDeps {
  client?: ParseClient;
  importTrack?: typeof importNormalisedTrack;
  draftEntry?: (track: Track, points: TrackPoints) => Promise<unknown>;
  now?: () => string;
}

let sequence = 0;
function nextId(): string {
  sequence += 1;
  return `queue-${String(sequence)}`;
}

const FINISHED: readonly QueueItemStatus[] = ['imported', 'duplicate', 'failed'];

/**
 * A lazily created, shared parse client.
 *
 * Created on first use rather than at module load so that importing this store
 * — which a route does, just by rendering — does not spawn a worker in a
 * session where nobody imports anything.
 */
let sharedClient: ParseClient | undefined;
function getSharedClient(): ParseClient {
  sharedClient ??= createParseClient();
  return sharedClient;
}

export const useImportQueue = create<ImportQueueState>()((set) => ({
  items: [],
  busy: false,
  offThread: true,

  async enqueue(files) {
    if (files.length === 0) {
      return;
    }
    const deps = queueDeps;
    const client = deps.client ?? getSharedClient();

    const queued: QueueItem[] = Array.from(files, (file) => ({
      id: nextId(),
      filename: file.name,
      sizeBytes: file.size,
      status: 'queued' as const,
      detail: [],
    }));

    set((state) => ({
      items: [...queued, ...state.items],
      busy: true,
      offThread: client.offThread,
    }));

    const update = (id: string, patch: Partial<QueueItem>): void => {
      set((state) => ({
        items: state.items.map((item) => (item.id === id ? { ...item, ...patch } : item)),
      }));
    };

    for (const [index, file] of files.entries()) {
      const item = queued[index];
      if (item === undefined) {
        continue;
      }

      update(item.id, { status: 'reading' });
      let bytes: Uint8Array;
      try {
        bytes = new Uint8Array(await file.arrayBuffer());
      } catch (cause) {
        update(item.id, {
          status: 'failed',
          message: 'This file could not be read from disk.',
          detail: [cause instanceof Error ? cause.message : String(cause)],
        });
        continue;
      }

      update(item.id, { status: 'parsing' });
      const parsed = await client.parse(file.name, bytes, deps.now?.());

      if (!parsed.ok) {
        update(item.id, {
          status: 'failed',
          message: parsed.message,
          detail: parsed.detail,
        });
        continue;
      }

      update(item.id, { status: 'saving', detail: parsed.notes });
      const result = await (deps.importTrack ?? importNormalisedTrack)(parsed.normalised, {
        requestPersistence: true,
        original: { filename: file.name, bytes },
      });

      switch (result.outcome) {
        case 'imported':
          update(item.id, {
            status: 'imported',
            detail: parsed.notes,
            ...(result.track === undefined ? {} : { track: result.track }),
            ...(result.message === undefined ? {} : { message: result.message }),
          });
          if (result.track !== undefined) {
            /*
             * Draft-and-approve — `PLAN.md` §6 Phase 5. Best-effort: a track
             * the pilot can already see in Flights is a successful import
             * regardless of whether drafting the logbook side of it works, so
             * a failure here is swallowed rather than turning an import row
             * that actually succeeded into one that reads as failed.
             */
            try {
              await (deps.draftEntry ?? draftLogbookEntryForTrack)(
                result.track,
                parsed.normalised.points,
              );
            } catch {
              // Swallowed by design — see the comment above.
            }
          }
          break;
        case 'skipped-duplicate':
          update(item.id, {
            status: 'duplicate',
            message: result.message ?? 'Already imported.',
            detail: parsed.notes,
            ...(result.track === undefined ? {} : { track: result.track }),
          });
          break;
        default:
          update(item.id, {
            status: 'failed',
            message: result.message ?? 'This track could not be saved.',
            detail: result.detail ?? parsed.notes,
          });
          break;
      }
    }

    set({ busy: false });
  },

  clearFinished() {
    set((state) => ({ items: state.items.filter((item) => !FINISHED.includes(item.status)) }));
  },

  reset() {
    set({ items: [], busy: false });
  },
}));

/**
 * Dependency overrides for tests.
 *
 * Module-level rather than part of the store's state: they are wiring, not
 * something the UI reads or renders, and putting them in the state would put
 * a worker handle into React's render path.
 */
let queueDeps: QueueDeps = {};

export function setImportQueueDeps(deps: QueueDeps): void {
  queueDeps = deps;
}

export function resetImportQueueDeps(): void {
  queueDeps = {};
}

export { FINISHED as FINISHED_STATUSES };
